import { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { onStatusChange } from "../../../utils/syncQueue";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";

import { listYears } from "../../../actions/yearActions";
import { listWeeks } from "../../../actions/weekActions";
import { listDays } from "../../../actions/dayActions";
import { getUserDetails, logout } from "../../../actions/userActions";

import {
  isDayComplete,
  getLatestDay,
  sortDaysByLogDate,
  findResumeTarget,
} from "../../../utils/dayProgress";
import { dateFormatter } from "../../../utils/UtilityFunctions";

import {
  ProfileIcon,
  YearIcon,
  WeekIcon,
  DiaryIcon,
  InsightsIcon,
  LogoutIcon,
  CreateDayIconV2,
  PreviousDay,
  SaveIcon,
  ValuesIcon,
} from "../../../components/ui/Icons/Icon";

import DonationBanner from "./DonationBanner";
import FeedbackBanner from "./FeedbackBanner";
import NavSearch from "./components/NavSearch";
import "./navbar.css";

function NavBar({ variant }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userInfo, loading: authLoading } = useSelector(
    (state) => state.userLogin,
  );
  const authResolved = authLoading === false;
  const { error: yearError, years } = useSelector((state) => state.yearList);
  const { error: weekError, weeks } = useSelector((state) => state.weekList);
  const { days } = useSelector((state) => state.dayList);

  useEffect(() => {
    dispatch(listDays());
    dispatch(listYears());
    dispatch(listWeeks());
  }, [dispatch, userInfo]);

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  const latestYearId = years?.at(-1)?._id;
  const latestWeekId = weeks?.at(-1)?._id;

  const yearUrl = yearError
    ? "#"
    : latestYearId
      ? `/year/${latestYearId}`
      : "/year/create";
  const weekUrl = weekError
    ? "#"
    : latestWeekId
      ? `/week/${latestWeekId}`
      : "/week/create";
  // The newest day by the date the user assigned it, not the order Mongo
  // returned it in — backfilling a logDate used to misroute the whole nav.
  const lastDay = getLatestDay(days);

  // The dot and the destination are one fact, deliberately. Computing them
  // separately let the dot promise a resume while the button opened a blank
  // form — the dot's whole job is to say where this button goes.
  const resumeTarget = useMemo(() => findResumeTarget(days), [days]);
  const dayUrl = resumeTarget ? `/today/${resumeTarget.id}` : "/today";

  const resumeLabel = !resumeTarget
    ? undefined
    : resumeTarget.logDate
      ? `Continue your unfinished day — ${dateFormatter(resumeTarget.logDate.slice(0, 10))}`
      : "Continue your unfinished day";

  // The logo goes to the app's home. For a signed-in user that is their
  // journal, not the marketing landing page.
  const brandUrl = userInfo ? dayUrl : "/";

  const [searchOpen, setSearchOpen] = useState(false);

  // "Previous" means the day before the one on screen, by date. It used to
  // return lastDay.id — the day you were already looking at — so the control
  // quietly did nothing. NavBar renders outside the route, so there is no
  // useParams here; read the id off the path instead.
  // pathname is "/today/<id>" on an open day, "/today" on the blank form.
  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentDayId =
    pathParts[0] === "today" && pathParts[1] ? pathParts[1] : null;

  const previousDayUrl = useMemo(() => {
    const ordered = sortDaysByLogDate(days, "asc");
    const cursor = currentDayId
      ? ordered.findIndex((d) => d.id === currentDayId)
      : ordered.length;
    // cursor === -1 means an id we do not know; fall through to no link rather
    // than guessing at the end of the list.
    if (cursor <= 0) return null;
    return `/today/${ordered[cursor - 1].id}`;
  }, [days, currentDayId]);

  const reduxCanCreateDay = !lastDay || isDayComplete(lastDay);

  const [liveCanCreateDay, setLiveCanCreateDay] = useState(null);
  useEffect(() => {
    const handler = (e) => setLiveCanCreateDay(e.detail.canCreateDay);
    window.addEventListener("nwd:day-validation", handler);
    return () => window.removeEventListener("nwd:day-validation", handler);
  }, []);

  const canCreateDay =
    liveCanCreateDay !== null ? liveCanCreateDay : reduxCanCreateDay;

  const [saveStatus, setSaveStatus] = useState("idle");
  const saveTimerRef = useRef(null);
  useEffect(() => {
    const unsubscribe = onStatusChange((event) => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      setSaveStatus(event.status);
      if (event.status === "saved") {
        saveTimerRef.current = setTimeout(() => {
          setSaveStatus("idle");
          saveTimerRef.current = null;
        }, 1800);
      }
    });
    return () => {
      unsubscribe();
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const handleNavSave = () => {
    window.dispatchEvent(new CustomEvent("nwd:save"));
  };

  const logoutHandler = () => {
    dispatch(logout());
    navigate("/");
  };

  const isActive = (prefixes) =>
    prefixes.some((p) => location.pathname.startsWith(p));

  const goToLandingSection = (sectionId) => {
    if (location.pathname === "/") {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    navigate(`/#${sectionId}`);
  };

  const marketingLinks = [
    { label: "How it works", sectionId: "how" },
    { label: "FAQs", sectionId: "faqs" },
    { label: "Your Data", sectionId: "faqs" },
    { label: "Blog", url: "/blog" },
  ];

  const navItems = [
    { Icon: YearIcon, label: "Map", url: yearUrl, prefixes: ["/year"] },
    { Icon: WeekIcon, label: "Planner", url: weekUrl, prefixes: ["/week"] },
    {
      Icon: DiaryIcon,
      label: "Journal",
      url: dayUrl,
      prefixes: ["/today"],
      flag: Boolean(resumeTarget),
    },
    {
      Icon: InsightsIcon,
      label: "Explore",
      url: "/insights",
      prefixes: ["/insights"],
    },
    {
      Icon: ProfileIcon,
      label: "Account",
      url: "/my-account/menu",
      prefixes: ["/my-account"],
    },
  ];

  return (
    <>
      <nav className="nwdnav">
        <DonationBanner />
        <FeedbackBanner />

        <div
          className={`nwdnav__bar${
            !userInfo ? " nwdnav__bar--marketing" : ""
          }`}
        >
          <button className="nwdnav__brand" onClick={() => navigate(brandUrl)}>
            <span className="nwdnav__brand-mark" aria-hidden="true">
              <ValuesIcon />
            </span>
            <span className="nwdnav__brand-word">NoWastedDays</span>
          </button>

          {!userInfo && (
            <div className="nwdnav__marketing-links">
              {marketingLinks.map((link) =>
                link.url ? (
                  <button
                    key={link.label}
                    className="nwdnav__marketing-link"
                    onClick={() => navigate(link.url)}
                  >
                    {link.label}
                  </button>
                ) : (
                  <button
                    key={link.label}
                    className="nwdnav__marketing-link"
                    onClick={() => goToLandingSection(link.sectionId)}
                  >
                    {link.label}
                  </button>
                ),
              )}
            </div>
          )}

          {authResolved && userInfo && (
            <div className="nwdnav__items">
              {navItems.map(({ Icon, label, url, prefixes, flag }) => (
                <button
                  key={label}
                  className={`nwdnav__item${
                    isActive(prefixes) ? " nwdnav__item--active" : ""
                  }${flag ? " nwdnav__item--flagged" : ""}`}
                  onClick={() => navigate(url)}
                  title={flag ? resumeLabel : undefined}
                >
                  <span className="nwdnav__icon">
                    <Icon />
                    {flag && (
                      <span className="nwdnav__item-flag" aria-hidden="true" />
                    )}
                  </span>
                  <span className="nwdnav__label">{label}</span>
                  {/* Appends to the visible label rather than replacing it, so
                      the accessible name states where the button goes:
                      "Journal — continue unfinished day". */}
                  {flag && (
                    <span className="nwdnav__sr">
                      {" "}— continue unfinished day
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="nwdnav__actions">
            {userInfo ? (
              <>
                <button
                  className={`nwdnav__search-btn${
                    searchOpen ? " nwdnav__search-btn--open" : ""
                  }`}
                  onClick={() => setSearchOpen((s) => !s)}
                  aria-label="Search journal"
                >
                  <span className="nwdnav__search-btn-icon">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                  </span>
                  <span className="nwdnav__search-btn-label">Search</span>
                </button>
                <button
                  className="nwdnav__donate"
                  onClick={() => navigate("/donate")}
                >
                  Donate
                </button>
                <button
                  className="nwdnav__logout-btn"
                  onClick={logoutHandler}
                  aria-label="Log out"
                >
                  <LogoutIcon />
                </button>
              </>
            ) : (
              <>
                <button
                  className="nwdnav__auth-btn"
                  onClick={() => navigate("/login")}
                >
                  <span>Log in</span>
                </button>
                <button
                  className="nwdnav__auth-btn nwdnav__auth-btn--primary"
                  onClick={() => navigate("/register")}
                >
                  <span>Get Started</span>
                </button>
              </>
            )}
          </div>
        </div>

        {searchOpen && userInfo && (
          <NavSearch data={days} onClose={() => setSearchOpen(false)} />
        )}
      </nav>

      {authResolved && userInfo && location.pathname.startsWith("/today") && (
        <nav className="nwdnav__tabs" aria-label="Quick actions">
          <button
            className={`nwdnav__tab${canCreateDay ? "" : " nwdnav__tab--disabled"}`}
            onClick={() => canCreateDay && navigate("/today")}
          >
            <span className="nwdnav__tab-icon">
              <CreateDayIconV2 />
            </span>
            <span className="nwdnav__tab-label">New Day</span>
          </button>

          <button
            className={`nwdnav__tab${previousDayUrl ? "" : " nwdnav__tab--disabled"}`}
            onClick={() => previousDayUrl && navigate(previousDayUrl)}
          >
            <span className="nwdnav__tab-icon">
              <PreviousDay />
            </span>
            <span className="nwdnav__tab-label">Previous</span>
          </button>

          <button
            className={`nwdnav__tab${searchOpen ? " nwdnav__tab--active" : ""}`}
            onClick={() => setSearchOpen((s) => !s)}
          >
            <span className="nwdnav__tab-icon">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </span>
            <span className="nwdnav__tab-label">Search</span>
          </button>

          <button
            className={`nwdnav__tab${
              saveStatus === "saving" ? " nwdnav__tab--saving" :
              saveStatus === "saved" ? " nwdnav__tab--saved" :
              saveStatus === "error" ? " nwdnav__tab--error" : ""
            }`}
            onClick={handleNavSave}
          >
            <span className="nwdnav__tab-icon">
              <SaveIcon />
            </span>
            <span className="nwdnav__tab-label">
              {saveStatus === "saving" ? "Saving…" :
               saveStatus === "saved" ? "Saved" :
               saveStatus === "error" ? "Retry" : "Save"}
            </span>
          </button>
        </nav>
      )}
    </>
  );
}

export default NavBar;
